import React, { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { PageHeader } from "@/components/common/Page";
import { Badge, templateStatusColor, reviewStatusColor } from "@/pages/legal/legalConfig";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { RichTextEditor } from "@/components/legal/RichTextEditor";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, FileText, ScrollText } from "lucide-react";

const inputCls = "border-[#2D3F62] bg-[#0B1120] text-slate-100 placeholder:text-slate-600 focus-visible:ring-cyan-500";

export default function Templates() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", category: "", jurisdiction: "Queensland, Australia", body_html: "" });

  const { data: meta } = useQuery({ queryKey: ["legal-meta"], queryFn: async () => (await api.get("/legal/meta")).data });
  const { data: rows } = useQuery({ queryKey: ["legal-templates"], queryFn: async () => (await api.get("/legal/templates")).data });

  const create = useMutation({
    mutationFn: async () => (await api.post("/legal/templates", form)).data,
    onSuccess: (t) => {
      qc.invalidateQueries({ queryKey: ["legal-templates"] });
      setOpen(false); setForm({ name: "", category: "", jurisdiction: "Queensland, Australia", body_html: "" });
      toast.success("Template created");
      navigate(`/legal/templates/${t.id}`);
    },
    onError: (e) => toast.error(formatApiError(e.response?.data?.detail)),
  });

  return (
    <div data-testid="legal-templates-page">
      <PageHeader title="Template Library" subtitle="Controlled legal document templates with immutable version history.">
        <Button onClick={() => setOpen(true)} data-testid="create-template-btn"
          className="bg-gradient-to-r from-cyan-500 to-sky-600 font-semibold text-slate-900 hover:from-cyan-400 hover:to-sky-500">
          <Plus className="mr-1.5 h-4 w-4" /> Create Template
        </Button>
      </PageHeader>

      {meta?.internal_notice && (
        <div className="mb-5 flex items-start gap-2 rounded-lg border border-amber-500/20 bg-amber-500/5 px-3 py-2.5 text-xs leading-relaxed text-amber-200/80" data-testid="internal-notice">
          <ScrollText className="mt-0.5 h-4 w-4 shrink-0" /> {meta.internal_notice}
        </div>
      )}

      <div className="overflow-hidden rounded-xl border border-[#1E2D4A]">
        <table className="w-full text-sm">
          <thead className="bg-[#0E1729] text-left text-[11px] uppercase tracking-wider text-slate-500">
            <tr>
              <th className="px-4 py-3">Template</th>
              <th className="px-4 py-3">Category</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Legal Review</th>
              <th className="px-4 py-3">Version</th>
              <th className="px-4 py-3 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#1E2D4A]">
            {(rows || []).map((t) => (
              <tr key={t.id} className="bg-[#111A2E]/40 hover:bg-[#17233B]/60" data-testid={`template-row-${t.id}`}>
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2 font-medium text-slate-100">
                    <FileText className="h-4 w-4 text-cyan-400" /> {t.name}
                  </div>
                  <div className="text-xs text-slate-500">{t.jurisdiction}</div>
                </td>
                <td className="px-4 py-3 text-slate-400">{t.category}</td>
                <td className="px-4 py-3"><Badge value={t.status} map={templateStatusColor} testId={`template-status-${t.id}`} /></td>
                <td className="px-4 py-3"><Badge value={t.legal_review_status} map={reviewStatusColor} /></td>
                <td className="px-4 py-3 font-mono text-xs text-slate-300">{t.current_version}</td>
                <td className="px-4 py-3 text-right">
                  <Button size="sm" variant="outline" data-testid={`open-template-${t.id}`}
                    className="border-[#2D3F62] bg-transparent text-slate-200 hover:bg-[#17233B]"
                    onClick={() => navigate(`/legal/templates/${t.id}`)}>Open</Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="border-[#1E2D4A] bg-[#111A2E] text-slate-100 sm:max-w-2xl" data-testid="create-template-dialog">
          <DialogHeader><DialogTitle>Create Template</DialogTitle></DialogHeader>
          <div className="grid gap-4">
            <div>
              <Label className="text-xs text-slate-400">Template name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className={`mt-1 ${inputCls}`} data-testid="template-name-input" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-slate-400">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                  <SelectTrigger className={`mt-1 ${inputCls}`} data-testid="template-category-select"><SelectValue placeholder="Select category" /></SelectTrigger>
                  <SelectContent className="border-[#2D3F62] bg-[#111A2E] text-slate-100">
                    {(meta?.categories || []).map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-400">Jurisdiction</Label>
                <Input value={form.jurisdiction} onChange={(e) => setForm({ ...form, jurisdiction: e.target.value })} className={`mt-1 ${inputCls}`} data-testid="template-jurisdiction-input" />
              </div>
            </div>
            <div>
              <Label className="text-xs text-slate-400">Template body (formatted — use the toolbar; insert {"{{variables}}"} where values merge in)</Label>
              <div className="mt-1"><RichTextEditor value={form.body_html} onChange={(html) => setForm({ ...form, body_html: html })} testId="template-body" /></div>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={() => create.mutate()} disabled={create.isPending || !form.name || !form.category}
              className="bg-gradient-to-r from-cyan-500 to-sky-600 font-semibold text-slate-900" data-testid="template-create-confirm">
              Create Draft
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
